import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Elements } from '@stripe/react-stripe-js';
import { X, CreditCard as CreditCardIcon, ArrowLeft, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import CreditCardForm from './CreditCardForm';
import { toast } from '../hooks/use-toast';
import BookingDetailsForm from './BookingDetailsForm';
import { Button } from './ui/button';
import axios from 'axios';
import { BACKEND_URL, getStripePublishableKey } from '../config';

const API_BASE = `${BACKEND_URL}/api`;

// Lazy Stripe.js loader.
// - `@stripe/stripe-js` is intentionally NOT imported at the top of this file.
//   The library auto-injects `<script src="https://js.stripe.com/...">` as a
//   side-effect of module evaluation. Importing it statically here would
//   pull both the library and the Stripe.js preload into every chunk that
//   transitively imports PaymentModal — including the homepage chunk that
//   eagerly-loaded PropertyDetailPage drags in. That preload sometimes fails
//   for bots / blocked networks and surfaces as an unhandled
//   "Failed to load Stripe.js" in Sentry (issue 7476660220).
// - Instead, we `import('@stripe/stripe-js')` only when a user actually opens
//   the modal, AND wrap the whole thing in a .catch() so the rejection never
//   bubbles to window.onunhandledrejection.
let _stripePromise = null;
const getStripePromise = () => {
  if (_stripePromise) return _stripePromise;
  _stripePromise = Promise.all([
    import('@stripe/stripe-js'),
    getStripePublishableKey(),
  ])
    .then(([{ loadStripe }, key]) => loadStripe(key))
    .catch((err) => {
      console.error('Stripe.js failed to load:', err);
      return null;
    });
  return _stripePromise;
};

const PaymentModal = ({ 
  isOpen, 
  onClose, 
  amount,
  propertyId,
  propertyTitle,
  property,
  checkIn,
  checkOut,
  guests,
  totalPrice,
  onPaymentSuccess,
  bookingMode = 'instant', // 'instant' or 'request'
  bookingType = 'full_property',
  roomNumber = null,
  roomName = null,
  // Pricing breakdown from BookingWidget (source of truth – matches Stripe total)
  pricePerNight = null,
  accommodationSubtotal = null,
  discountAmount = 0,
  promoCode = null,
  // Parking add-on — set by BookingWidget when the guest opts in. The
  // slot label / total are just for display inside the modal; the actual
  // charge is re-verified server-side by /payments/create-payment-intent.
  parkingRequested = false,
  parkingCarSize = null,
  parkingSlot = null,
  parkingTotal = 0,
}) => {
  const { t } = useTranslation();
  // Normalize guests to an integer (adults + children) regardless of input shape
  const totalGuests = (typeof guests === 'object' && guests !== null)
    ? (Number(guests.adults) || 0) + (Number(guests.children) || 0)
    : Number(guests) || 1;
  const [step, setStep] = useState(1); // 1 = Guest Details, 2 = Payment
  const [guestDetails, setGuestDetails] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [loading, setLoading] = useState(false);

  // Lazy-build the Stripe promise the first time this modal mounts. Stable
  // ref across re-renders so <Elements> stays mounted. Never fires on
  // routes that don't render PaymentModal.
  const [stripePromise] = useState(() => (isOpen ? getStripePromise() : null));
  // Backfill the promise the first time the modal opens (covers the case
  // where the parent keeps the modal mounted with isOpen=false initially).
  const effectiveStripePromise = stripePromise || (isOpen ? getStripePromise() : null);

  // Pull logged-in user's profile from localStorage so we can pre-fill (or
  // skip) the guest details form. Saves a redundant data-entry step for
  // anyone who's already signed up — name/email/phone are already on file.
  const currentUser = useMemo(() => {
    try {
      const raw = localStorage.getItem('user_data');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, [isOpen]); // re-read whenever the modal opens

  const guestDefaults = useMemo(() => {
    if (!currentUser) return null;
    return {
      fullName: currentUser.full_name || '',
      firstName: currentUser.first_name || '',
      lastName: currentUser.last_name || '',
      email: currentUser.email || '',
      phone: currentUser.phone || '',
    };
  }, [currentUser]);

  // Auto-skip the guest details step when the profile already has every
  // required field. The user can still go back via the "Edit" button on
  // the payment screen if they want to use a different name/email.
  useEffect(() => {
    if (!isOpen) return;
    if (!guestDefaults) return;
    const fullName = (guestDefaults.fullName || `${guestDefaults.firstName || ''} ${guestDefaults.lastName || ''}`).trim();
    const phone = (guestDefaults.phone || '').trim();
    const email = (guestDefaults.email || '').trim();
    if (fullName && email && phone && !guestDetails) {
      setGuestDetails({
        customerName: fullName,
        customerEmail: email,
        customerPhone: phone,
        message: '',
      });
      setStep(2);
    }
  }, [isOpen, guestDefaults]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGuestDetailsSubmit = (details) => {
    setGuestDetails(details);
    setStep(2); // Move to payment step
  };

  const handleBackToDetails = () => {
    setStep(1);
  };

  const handleStripeCheckout = async () => {
    try {
      setLoading(true);
      
      // Detect booking source from referrer or current path
      let bookingSource = 'vacation'; // default
      const referrer = document.referrer;
      if (referrer.includes('/students')) {
        bookingSource = 'students';
      } else if (referrer.includes('/medical')) {
        bookingSource = 'medical';
      } else if (referrer.includes('/insurance')) {
        bookingSource = 'insurance';
      }
      
      const userToken = localStorage.getItem('user_token') || localStorage.getItem('token');
      const response = await axios.post(`${API_BASE}/payments/checkout/session`, {
        property_id: propertyId,
        check_in: checkIn?.toISOString(),
        check_out: checkOut?.toISOString(),
        guests: totalGuests,
        customer_name: guestDetails?.customerName,
        customer_email: guestDetails?.customerEmail,
        customer_phone: guestDetails?.customerPhone,
        message: guestDetails?.message,
        origin_url: window.location.origin,
        booking_source: bookingSource, // Track which page they booked from
        booking_mode: bookingMode, // 'instant' or 'request' - determines payment capture
        booking_type: bookingType, // 'full_property' or 'single_room'
        room_number: roomNumber,
        room_name: roomName,
        promo_code: promoCode || null,
      }, {
        headers: userToken ? { Authorization: `Bearer ${userToken}` } : {},
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      // Surface the ID-verification gate so the user knows why booking failed
      const detail = error?.response?.data?.detail;
      if (error?.response?.status === 403 && detail?.error_code === 'id_verification_required') {
        toast({
          title: 'ID verification required',
          description: detail.message || 'Please verify your ID before booking.',
          variant: 'destructive',
        });
        onClose?.();
      } else if (error?.response?.status === 403 && detail?.error_code === 'account_removed') {
        toast({
          title: 'Account restricted',
          description: detail.message || 'Your account is currently restricted from booking. Please contact support.',
          variant: 'destructive',
          duration: 8000,
        });
        onClose?.();
      } else if (error?.response?.status === 401) {
        toast({
          title: 'Please sign in',
          description: detail?.message || 'Your session expired. Please sign in again.',
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Checkout failed',
          description: typeof detail === 'string' ? detail : (detail?.message || 'Please try again.'),
          variant: 'destructive',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCardPaymentSuccess = (paymentIntent) => {
    console.log('Payment successful:', paymentIntent);
    if (onPaymentSuccess) {
      onPaymentSuccess(paymentIntent);
    }
    onClose();
  };

  const handleClose = () => {
    setStep(1);
    setGuestDetails(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            {step === 1 ? (
              <>
                <CreditCardIcon className="w-6 h-6 text-blue-600" />
                {t('booking.guestInformation')}
              </>
            ) : (
              <>
                <CreditCardIcon className="w-6 h-6 text-blue-600" />
                {t('booking.confirmAndPay')}
              </>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="py-4">
          {/* Step 1: Guest Details Form */}
          {step === 1 && (
            <BookingDetailsForm
              onSubmit={handleGuestDetailsSubmit}
              loading={loading}
              submitButtonText={t('booking.proceedToPayment')}
              property={property || { title: propertyTitle }}
              checkIn={checkIn}
              checkOut={checkOut}
              guests={guests}
              totalPrice={totalPrice || amount}
              showPropertySummary={true}
              bookingType={bookingType}
              roomName={roomName}
              roomNumber={roomNumber}
              defaults={guestDefaults}
            />
          )}

          {/* Step 2: Payment */}
          {step === 2 && (
            <div className="space-y-6">
              {/* Back Button — labeled "Edit guest details" so logged-in
                  users who auto-skipped step 1 know they can override the
                  pre-filled name/email/phone if they want. */}
              <Button
                onClick={handleBackToDetails}
                variant="outline"
                size="sm"
                className="mb-4"
                data-testid="payment-edit-guest-details"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Edit guest details
              </Button>

              {/* Guest Info Summary */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-gray-900 mb-3">{t('booking.guestInformation')}</h3>
                <div className="space-y-2 text-sm">
                  <p><strong>{t('booking.fullName')}:</strong> {guestDetails?.customerName}</p>
                  <p><strong>{t('booking.email')}:</strong> {guestDetails?.customerEmail}</p>
                  <p><strong>{t('booking.phone')}:</strong> {guestDetails?.customerPhone}</p>
                  {guestDetails?.message && (
                    <p><strong>{t('booking.message')}:</strong> {guestDetails.message}</p>
                  )}
                </div>
              </div>

              {/* Property Summary */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-gray-900 mb-2">{propertyTitle || property?.title}</h3>
                <div className="text-sm text-gray-600 space-y-1">
                  {checkIn && checkOut && (
                    <p>
                      <strong>{t('search.checkIn')}:</strong> {checkIn.toLocaleDateString()} — {checkOut.toLocaleDateString()}
                    </p>
                  )}
                  {guests && <p><strong>{t('common.guests')}:</strong> {guests.adults + guests.children}</p>}
                  <p className="text-lg font-bold text-gray-900 mt-2">
                    {t('common.total')}: CA${(totalPrice || amount).toFixed(2)}
                  </p>
                </div>
              </div>

              {/* Cancellation Policy Warning */}
              {property?.cancellation_policy && (
                <div className={`mb-4 border rounded-lg p-3 text-xs ${
                  property.cancellation_policy === 'non_refundable' 
                    ? 'bg-red-50 border-red-200' 
                    : property.cancellation_policy === 'strict'
                      ? 'bg-orange-50 border-orange-200'
                      : 'bg-blue-50 border-blue-200'
                }`}>
                  <div className="flex items-start gap-2">
                    <span className="text-base flex-shrink-0">
                      {property.cancellation_policy === 'non_refundable' && '🚫'}
                      {property.cancellation_policy === 'strict' && '🔒'}
                      {property.cancellation_policy === 'moderate' && '⚖️'}
                      {property.cancellation_policy === 'flexible' && 'ℹ️'}
                    </span>
                    <div className="flex-1">
                      <p className={`font-semibold mb-1 ${
                        property.cancellation_policy === 'non_refundable' 
                          ? 'text-red-900' 
                          : property.cancellation_policy === 'strict'
                            ? 'text-orange-900'
                            : 'text-blue-900'
                      }`}>
                        Cancellation: {property.cancellation_policy === 'non_refundable' ? 'Non-Refundable' : property.cancellation_policy.charAt(0).toUpperCase() + property.cancellation_policy.slice(1)}
                      </p>
                      <p className={`${
                        property.cancellation_policy === 'non_refundable' 
                          ? 'text-red-800' 
                          : property.cancellation_policy === 'strict'
                            ? 'text-orange-800'
                            : 'text-blue-800'
                      }`}>
                        {property.cancellation_policy === 'flexible' && 'Free cancellation up to 24 hours before check-in'}
                        {property.cancellation_policy === 'moderate' && 'Free cancellation up to 5 days before check-in'}
                        {property.cancellation_policy === 'strict' && 'Free cancellation up to 14 days before check-in'}
                        {property.cancellation_policy === 'non_refundable' && 'No cancellations or refunds allowed under any circumstances.'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Payment Method Tabs */}
              <Tabs value={paymentMethod} onValueChange={setPaymentMethod} className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-6">
                  <TabsTrigger value="card" className="flex items-center gap-2">
                    <CreditCardIcon className="w-4 h-4" />
                    Credit Card
                  </TabsTrigger>
                  <TabsTrigger value="checkout">
                    Stripe Checkout
                  </TabsTrigger>
                </TabsList>

                {/* Credit Card Tab */}
                <TabsContent value="card" className="mt-0">
                  {/* Deferred-intent Elements: mode/amount/currency here MUST
                      match the PaymentIntent the backend creates on submit
                      (totalPrice already includes the parking add-on). This
                      enables Apple Pay / Google Pay / Link in PaymentElement. */}
                  <Elements
                    stripe={effectiveStripePromise}
                    options={{
                      mode: 'payment',
                      amount: Math.round((totalPrice || amount) * 100),
                      currency: 'cad',
                      // The backend attaches the payment to a Stripe Customer
                      // with setup_future_usage='off_session' (enables the
                      // damage-deposit re-auth). Deferred Elements must
                      // declare the same value or Stripe rejects the confirm.
                      setupFutureUsage: 'off_session',
                      appearance: { theme: 'stripe' },
                    }}
                  >
                    <CreditCardForm
                      amount={totalPrice || amount}
                      propertyId={propertyId}
                      propertyTitle={propertyTitle || property?.title}
                      checkIn={checkIn?.toISOString()}
                      checkOut={checkOut?.toISOString()}
                      guests={totalGuests}
                      pricePerNight={pricePerNight}
                      accommodationSubtotal={accommodationSubtotal}
                      discountAmount={discountAmount}
                      bookingType={bookingType}
                      roomNumber={roomNumber}
                      roomName={roomName}
                      customerName={guestDetails?.customerName}
                      customerEmail={guestDetails?.customerEmail}
                      customerPhone={guestDetails?.customerPhone}
                      message={guestDetails?.message}
                      parkingRequested={parkingRequested}
                      parkingCarSize={parkingCarSize}
                      onSuccess={handleCardPaymentSuccess}
                      onError={(error) => console.error('Payment error:', error)}
                      buttonText={`Pay CA$${(totalPrice || amount).toFixed(2)}`}
                    />
                  </Elements>
                </TabsContent>

                {/* Stripe Checkout Tab */}
                <TabsContent value="checkout" className="mt-0">
                  <div className="space-y-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h4 className="font-semibold text-blue-900 mb-2">Stripe Checkout</h4>
                      <p className="text-sm text-blue-700 mb-4">
                        You'll be redirected to Stripe's secure payment page. Supports credit cards, 
                        Apple Pay, Google Pay, and more.
                      </p>
                      <ul className="text-xs text-blue-600 space-y-1">
                        <li>✓ Multiple payment methods</li>
                        <li>✓ Saved payment methods</li>
                        <li>✓ International cards accepted</li>
                      </ul>
                    </div>

                    <Button
                      onClick={handleStripeCheckout}
                      disabled={loading}
                      className="w-full bg-[#635BFF] hover:bg-[#5851EA] text-white font-semibold py-6 rounded-lg transition-colors"
                    >
                      {loading ? (
                        <span className="flex items-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Redirecting...
                        </span>
                      ) : (
                        'Continue to Stripe Checkout'
                      )}
                    </Button>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentModal;
